import React, { useCallback } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { CopyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const copyButtonVariants = cva('inline-flex items-center bg-transparent disabled:opacity-50', {
  variants: {
    iconPosition: { left: 'flex-row', right: 'flex-row-reverse', center: 'flex-col' },
    size: { sm: 'text-sm', md: 'text-base', lg: 'text-lg' },
    variant: {
      default: 'text-muted-foreground hover:text-foreground',
      ghost: 'text-muted-foreground hover:bg-subtle hover:text-foreground',
      outline: 'border border-border text-muted-foreground hover:bg-subtle hover:text-foreground',
    },
  },
  defaultVariants: { iconPosition: 'right', size: 'md', variant: 'default' },
});

const textVariants = cva('inline min-w-0 cursor-pointer text-muted-foreground', {
  variants: {
    iconPosition: { left: 'ml-2', right: 'mr-2', center: 'mx-0' },
    hasIcon: { true: '', false: '' },
    size: { sm: 'text-sm', md: 'text-base', lg: 'text-lg' },
  },
  compoundVariants: [{ hasIcon: false, className: 'mx-0' }],
  defaultVariants: { hasIcon: true, size: 'md' },
});

export type CopyButtonProps = {
  data: string;
  className?: string;
  /** Box size of the button itself — `className` styles the wrapper, which is what positions it. */
  buttonSize?: ButtonProps['size'];
  buttonVariant?: ButtonProps['variant'];
  onCopy?: () => void;
  children?: React.ReactNode;
  disabled?: boolean;
  iconSize?: number;
  hideIcon?: boolean;
  icon?: React.ReactNode;
  tooltip?: React.ReactNode;
  tooltipSide?: React.ComponentProps<typeof InfoTooltip>['side'];
} & VariantProps<typeof copyButtonVariants>;
const CopyButton = ({
  className,
  buttonSize = 'icon-sm',
  buttonVariant = 'ghost-neutral',
  data,
  children,
  disabled,
  iconSize = 18,
  hideIcon = false,
  iconPosition = 'right',
  size = 'md',
  variant = 'default',
  icon,
  tooltip,
  tooltipSide = 'bottom',
  ...props
}: CopyButtonProps) => {
  const { t } = useTranslation();

  const copyToClipboard = useCallback(
    (e) => {
      e.preventDefault();
      navigator.clipboard.writeText(data);
      e.target.focus();
      if (!disabled) {
        toast.success(t('common.copied'));
      }
    },
    [disabled, t, data]
  );

  const RenderIcon = () => {
    if (icon) {
      return icon;
    }
    return <CopyIcon className="stroke-muted-foreground text-muted-foreground" size={iconSize} />;
  };

  const button = (
    <Button
      data-testid="button-copy"
      type="button"
      variant={buttonVariant}
      size={children ? undefined : buttonSize}
      className={cn(copyButtonVariants({ iconPosition, size, variant }))}
      aria-label={t('common.copy')}
      onClick={copyToClipboard}
      disabled={disabled}
      {...props}
    >
      {!hideIcon && iconPosition === 'left' && <RenderIcon />}
      {children ? (
        <span
          className={cn(
            'text-sm leading-5 font-semibold',
            textVariants({ iconPosition, hasIcon: !hideIcon })
          )}
        >
          {children}
        </span>
      ) : null}
      {!hideIcon && iconPosition === 'right' && <RenderIcon />}
      {!hideIcon && iconPosition === 'center' && <RenderIcon />}
    </Button>
  );

  return (
    <span className={cn(className)}>
      {tooltip ? (
        <InfoTooltip content={tooltip} side={tooltipSide} className="z-3000">
          <span className="inline-flex">{button}</span>
        </InfoTooltip>
      ) : (
        button
      )}
    </span>
  );
};

export default CopyButton;
